import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../homepage/navbar/Navbar';
import { useThemeContext } from '../../context/theme-context';
import './admin.css';

const AdminGeneralContacts = () => {
  const { themeState } = useThemeContext();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination & Filtering State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

  useEffect(() => {
    fetchContacts();
  }, [page, limit, startDate, endDate]);

  const fetchContacts = async () => {
    try {
      setError(null);
      setLoading(true);
      const queryParams = new URLSearchParams({
        page,
        limit,
        search,
        startDate,
        endDate
      });

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/contacts/general?${queryParams.toString()}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include'
      });

      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      const json = await res.json();
      if (res.ok) {
        setContacts(json.data || []);
        if (json.pagination) {
          setTotalPages(json.pagination.pages || 1);
          setTotalCount(json.pagination.total || 0);
        }
      } else {
        setError(json.message || 'Failed to load general contacts');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchContacts();
  };

  const handleClearFilters = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
    setPage(1);
    // Explicitly fetch without variables
    setTimeout(() => {
      fetchContacts();
    }, 0);
  };

  const handleDeleteContact = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the submission from "${name}"?`)) {
      return;
    }

    try {
      setError(null);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/contacts/general/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include'
      });

      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      const json = await res.json();
      if (res.ok) {
        fetchContacts();
      } else {
        setError(json.message || 'Failed to delete contact');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <main className={`${themeState.primary} ${themeState.background} admin__page`}>
      <Navbar />

      <section className="admin__container">
        <br/>
        <div className="admin__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button className="btn sm" onClick={() => navigate('/admin')}>← Back</button>
            <h2>General Contacts</h2>
          </div>
        </div>
        <p className="muted">All general contact form submissions.</p>

        {/* FILTERS TOOLBAR */}
        <div className="admin__filters-bar" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: '1.5rem 0', padding: '1rem', background: 'var(--color-light)', borderRadius: '8px' }}>
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '0.5rem', flexGrow: 1 }}>
            <input 
              type="text" 
              placeholder="Search by name or email..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid rgba(0,0,0,0.1)', flexGrow: 1, minWidth: '180px' }}
            />
            <button type="submit" className="btn primary sm" style={{ border: 'none' }}>Search</button>
          </form>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>From:</label>
              <input 
                type="date" 
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                style={{ padding: '0.4rem', borderRadius: '4px', border: '1px solid rgba(0,0,0,0.1)', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>To:</label>
              <input 
                type="date" 
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                style={{ padding: '0.4rem', borderRadius: '4px', border: '1px solid rgba(0,0,0,0.1)', fontFamily: 'inherit' }}
              />
            </div>
            {(search || startDate || endDate) && (
              <button 
                type="button" 
                className="btn sm" 
                onClick={handleClearFilters}
                style={{ background: 'rgba(0,0,0,0.06)', border: 'none' }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {error && <div className="admin__error">{error}</div>}
        {loading && <div className="admin__loading">Loading...</div>}

        {!loading && (
          <>
            <div className="admin__table-wrap">
              <table className="admin__table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Subject</th>
                    <th>Message</th>
                    <th>Created At</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length === 0 && (
                    <tr>
                      <td colSpan="7" style={{textAlign: 'center', padding: '2rem'}}>
                        No submissions match the current filters.
                      </td>
                    </tr>
                  )}
                  {contacts.map(contact => (
                    <tr key={contact._id}>
                      <td>{contact.name}</td>
                      <td>{contact.email}</td>
                      <td>{contact.mobile || '-'}</td>
                      <td>{contact.subject || '-'}</td>
                      <td className="admin__message">{contact.message || '-'}</td>
                      <td>{new Date(contact.createdAt).toLocaleString()}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button 
                          className="btn sm" 
                          onClick={() => handleDeleteContact(contact._id, contact.name)}
                          style={{ background: 'rgba(176,0,32,0.15)', color: '#b00020', border: 'none', cursor: 'pointer', margin: '0 auto' }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PAGINATION FOOTER */}
            {totalPages > 1 && (
              <div className="admin__pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <span style={{ fontSize: '0.9rem', color: 'var(--color-dark)', opacity: 0.8 }}>
                  Showing {contacts.length} of {totalCount} submissions
                </span>
                
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <button 
                    className="btn sm" 
                    disabled={page === 1}
                    onClick={() => setPage(prev => Math.max(1, prev - 1))}
                    style={{ border: 'none', cursor: 'pointer' }}
                  >
                    Prev
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                    <button 
                      key={pageNum}
                      className={`btn sm ${page === pageNum ? 'primary' : ''}`}
                      onClick={() => setPage(pageNum)}
                      style={{ border: 'none', minWidth: '32px', display: 'flex', justifyContent: 'center' }}
                    >
                      {pageNum}
                    </button>
                  ))}
                  
                  <button 
                    className="btn sm" 
                    disabled={page === totalPages}
                    onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                    style={{ border: 'none', cursor: 'pointer' }}
                  >
                    Next
                  </button>

                  <select 
                    value={limit} 
                    onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }}
                    style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid rgba(0,0,0,0.1)', fontFamily: 'inherit', marginLeft: '0.5rem' }}
                  >
                    <option value={5}>5 / page</option>
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
};

export default AdminGeneralContacts;
